import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { authService, logSessionExpired } from "../services/auth";
import { isLocalDevMode } from "../config/cognito";
import { logger } from "../utils/logger";
import axios from "axios";

// Default timeout for all axios requests (30s). Individual requests can override.
axios.defaults.timeout = 30_000;

// Auth header on every request, registered at import: React runs a provider's
// effects after its children's, so an effect-registered interceptor missed the
// first queries children fire on mount, and they went out unauthenticated (401).
axios.interceptors.request.use((config) => {
  const { token } = authService.getStoredAuth();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

import {
  authStateAtom,
  clearAuthStateAtom,
  sessionExpiredAtom,
  tokenAtom,
  userIdAtom,
  isAdminAtom,
  adminUserIdAtom,
  isAuthenticatedAtom,
  type AuthState,
} from "../atoms/authAtoms";

/**
 * Shared refresh promise — serializes concurrent token refresh attempts.
 * If a refresh is already in-flight, subsequent callers await the same promise
 * instead of issuing duplicate refresh requests.
 */
let refreshPromise: Promise<Awaited<ReturnType<typeof authService.refreshToken>> | null> | null =
  null;

async function serializedRefresh(): Promise<Awaited<
  ReturnType<typeof authService.refreshToken>
> | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = authService
    .refreshToken()
    .catch(() => null)
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

/** Retry a refresh up to `maxRetries` times with exponential backoff. */
async function refreshWithRetry(
  maxRetries = 2,
): Promise<Awaited<ReturnType<typeof authService.refreshToken>> | null> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const result = await serializedRefresh();
    if (result) return result;
    if (attempt < maxRetries) {
      // Exponential backoff: 1s, 2s
      await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
    }
  }
  return null;
}

/**
 * AuthProvider - handles token refresh and axios interceptors.
 * Local dev mode: skips all token logic (backend skips auth).
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useAtom(authStateAtom);
  const clearAuth = useSetAtom(clearAuthStateAtom);
  const setSessionExpired = useSetAtom(sessionExpiredAtom);
  const navigate = useNavigate();

  const handleLogout = useCallback(
    async (expired = false) => {
      if (expired) {
        setSessionExpired(true);
        return;
      }
      if (authState.userId) await authService.logout();
      clearAuth();
      navigate("/login");
    },
    [authState.userId, clearAuth, navigate, setSessionExpired],
  );

  // Token refresh every 60s (production only) with retry
  useEffect(() => {
    if (isLocalDevMode() || !authState.token) return;

    const refresh = async () => {
      const state = authService.evaluateSession();
      if (state === "active") return;
      if (state === "refreshable") {
        const newToken = await refreshWithRetry();
        if (newToken) {
          setAuthState((prev) => ({ ...prev, ...newToken }));
          return;
        }
        // #723: the refresh didn't recover, but the session is still
        // "refreshable" (a refresh token is present). This is a TRANSIENT
        // failure (5xx/timeout/network) — authService.refreshToken only clears
        // cookies on a genuine 401/403. Don't log the user out mid-work and
        // lose unsaved annotations; retry on the next interval. A genuinely
        // invalid refresh token clears the cookies in refreshToken, so the next
        // interval evaluates to "absent"/"expired" and logs out correctly.
        logger.warn("[auth] token refresh failed transiently; retrying next interval");
        return;
      }
      // "expired" or "absent" — the session is genuinely over. An impersonation
      // session reaches here only once its token actually lapses (the 60-min
      // cap), never on the missing refresh token that minting deliberately
      // leaves behind.
      logSessionExpired("auth-interval", state);
      handleLogout(true);
    };

    refresh();
    const interval = setInterval(refresh, 60000);
    return () => clearInterval(interval);
  }, [authState.token, authState.refreshToken, handleLogout, setAuthState]);

  // Axios: retry on 401.
  // Always register a fresh interceptor and eject on cleanup — safe under StrictMode.
  useEffect(() => {
    const resInt = axios.interceptors.response.use(
      (r) => r,
      async (error) => {
        if (isLocalDevMode()) return Promise.reject(error);

        const req = error.config;
        if (error.response?.status === 401 && !req._retry) {
          req._retry = true;
          const newToken = await serializedRefresh();
          if (newToken) {
            setAuthState((prev) => ({ ...prev, ...newToken }));
            req.headers.Authorization = `Bearer ${newToken.token}`;
            return axios(req);
          }
          // 401 with no refresh path (impersonation, or refresh also lapsed) —
          // genuinely the end of the session, so the modal is correct here.
          logSessionExpired("axios-401", "expired");
          handleLogout(true);
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(resInt);
    };
  }, [handleLogout, setAuthState]);

  return <>{children}</>;
};

/** useAuth hook - provides auth state and actions */
export const useAuth = () => {
  const [authState, setAuthState] = useAtom(authStateAtom);
  const clearAuth = useSetAtom(clearAuthStateAtom);
  const navigate = useNavigate();

  const login = useCallback(
    async (userId: string, password: string) => {
      const r = await authService.login(userId, password);
      setAuthState({
        token: r.token,
        refreshToken: r.refreshToken,
        userId,
        isAdmin: r.isAdmin,
        adminUserId: r.isAdmin ? userId : null,
        userType: r.userType,
      });
    },
    [setAuthState],
  );

  const logout = useCallback(async () => {
    if (authState.userId) await authService.logout();
    clearAuth();
    navigate("/login");
  }, [authState.userId, clearAuth, navigate]);

  /**
   * Re-mint the current session from the server-side profile and publish the
   * refreshed credentials to every consumer. Most refreshes are scheduled by
   * AuthProvider; callers with a server-confirmed scope change (such as a Live
   * Upload receipt after company reassignment) can use this immediate path.
   */
  const refreshAuth = useCallback(async () => {
    const refreshed = await serializedRefresh();
    if (!refreshed) return null;
    setAuthState((prev) => ({ ...prev, ...refreshed }));
    return refreshed;
  }, [setAuthState]);

  return {
    isAuthenticated: Boolean(authState.token && authState.userId),
    ...authState,
    login,
    logout,
    refreshAuth,
    setAuthState,
  };
};

/** Granular hooks for components that only need specific auth fields */
export const useToken = () => useAtomValue(tokenAtom);
export const useUserId = () => useAtomValue(userIdAtom);
export const useIsAdmin = () => useAtomValue(isAdminAtom);
export const useAdminUserId = () => useAtomValue(adminUserIdAtom);
export const useIsAuthenticated = () => useAtomValue(isAuthenticatedAtom);

export type { AuthState };
